import { FormField } from "@/overlay/components/form-field";
import {
  type EnsOption,
  EnsOtherMembers,
  allMembers,
  allQuantile,
  band20p,
  band40p,
  band60p,
  band80p,
  band100p,
  constructStringFromMultiSelectEnsembles,
  deconstructEnsStringFromMultiSelectEnsembles,
} from "@/utility/ensemble";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import type { ModelSchema } from "@mm/api-layers.meteomatics.com";
import { MmEnsdropdown } from "@mm/zeus-react";
import { useMemo } from "react";

export let exportedMemberList: { id: string; title: string }[] = [];

type GroupedEnsembleMembersProps = {
  ensSelect: EnsOption | undefined;
  selectedModelSchema: ModelSchema;
  onChange: (propsValue: { ens_select: EnsOption }) => void;
};

export function GroupedEnsembleMembers({ ensSelect, selectedModelSchema, onChange }: GroupedEnsembleMembersProps) {
  const { i18n } = useLingui();

  const memberList = useMemo(() => {
    const members =
      (selectedModelSchema.num_ensemble_members ?? 0) > 0 ? [{ id: allMembers.toString(), title: t`Members` }] : [];
    exportedMemberList = members;
    return [...members];
  }, [selectedModelSchema.num_ensemble_members, i18n.locale]);

  const bandList = useMemo(() => {
    return [
      { id: band100p, title: t`100% band` },
      { id: band80p, title: t`80% band` },
      { id: band60p, title: t`60% band` },
      { id: band40p, title: t`40% band` },
      { id: band20p, title: t`20% band` },
      { id: EnsOtherMembers.spread, title: t`Spread` },
      { id: EnsOtherMembers.mean, title: t`Mean` },
      { id: EnsOtherMembers.median, title: t`Median` },
    ];
  }, [i18n.locale]);

  const quantileList = useMemo(() => {
    return [{ id: allQuantile, title: t`Quantiles` }];
  }, [i18n.locale]);

  const handleSelectionChanged = (
    event: CustomEvent<{ members: { id: string }[]; quantiles: { id: string }[]; others: { id: string }[] }>,
  ) => {
    const { members, quantiles, others } = event.detail;

    const selectedOptions = [...others].map((item) => item.id);

    if (members.some((member) => member.id === allMembers.toString())) {
      selectedOptions.push(allMembers.toString());
    }

    if (quantiles.some((quantile) => quantile.id === allQuantile)) {
      selectedOptions.push(allQuantile);
    }

    if (selectedOptions.length === 0) {
      selectedOptions.push(band100p, EnsOtherMembers.mean);
    }

    onChange({
      ens_select: constructStringFromMultiSelectEnsembles(...selectedOptions),
    });
  };

  return (
    <FormField label={t`Ens Members`} noSemanticLinking alignTop>
      <MmEnsdropdown
        members={memberList}
        selected={deconstructEnsStringFromMultiSelectEnsembles(ensSelect)}
        quantiles={quantileList}
        others={bandList}
        onSelectionChanged={handleSelectionChanged}
      />
    </FormField>
  );
}
